import useStore from "../Store/Store";
import { logger } from "./Logger";

/**
 * Builds mappings between repoData objects and simulations/simulation steps
 * This creates a two-way relationship for navigation between code and simulations
 */
export function buildSimulationMappings(): RepoDataSimulationMappings {
  const { simulationsState, repoData } = useStore.getState();
  const mappings: RepoDataSimulationMappings = {};

  logger.debug.info("Building simulation mappings...");

  // Iterate through all simulations
  Object.entries(simulationsState.simulations).forEach(
    ([simulationName, simulation]) => {
      // Iterate through all simulation steps
      simulation.simSteps.forEach((simStep) => {
        // Check if this step has source code mapping
        if (simStep.sourceCodeMapping) {
          const { filePath, startLine, endLine } = simStep.sourceCodeMapping;

          // Find matching repoData objects
          const matchingRepoPaths = findMatchingRepoDataPaths(
            filePath,
            parseInt(startLine),
            parseInt(endLine),
            repoData
          );

          // Create mappings for each matching repoData object
          matchingRepoPaths.forEach((repoPath) => {
            const mapping: SimulationMapping = {
              simulationName,
              simStepId: simStep.simStepId,
              simStepLabel: simStep.simStepLabel,
            };

            if (!mappings[repoPath]) {
              mappings[repoPath] = [];
            }

            // Check for duplicates before adding
            const exists = mappings[repoPath].some(
              (m) =>
                m.simulationName === simulationName &&
                m.simStepId === simStep.simStepId
            );

            if (!exists) {
              mappings[repoPath].push(mapping);
            }
          });
        }
      });
    }
  );

  logger.debug.info(
    `Built simulation mappings for ${
      Object.keys(mappings).length
    } repoData objects`
  );
  return mappings;
}

/**
 * Adds a reverse mapping when a simulation step is created
 * This updates the repoData object to include a reference to the simulation step
 */
export function addReverseMapping(
  simulationName: string,
  simStep: SimStep
): void {
  if (!simStep.sourceCodeMapping) {
    logger.debug.info(
      `SimStep ${simStep.simStepId} has no sourceCodeMapping, skipping reverse mapping`
    );
    return;
  }

  const { filePath, startLine, endLine } = simStep.sourceCodeMapping;
  const { repoData } = useStore.getState();

  // Find matching repoData objects
  const matchingRepoPaths = findMatchingRepoDataPaths(
    filePath,
    parseInt(startLine),
    parseInt(endLine),
    repoData
  );

  logger.debug.info(
    `Adding reverse mapping for SimStep ${simStep.simStepId} to ${matchingRepoPaths.length} repoData objects`
  );

  // Add simulation reference to each matching repoData object
  matchingRepoPaths.forEach((repoPath) => {
    const repoDataObject = repoData[repoPath];
    if (repoDataObject) {
      // Get existing simulation mappings or create new array
      const existingMappings = repoDataObject.simulationMappings || [];

      // Create new mapping
      const newMapping: SimulationMapping = {
        simulationName,
        simStepId: simStep.simStepId,
        simStepLabel: simStep.simStepLabel,
      };

      // Check for duplicates
      const exists = existingMappings.some(
        (m: SimulationMapping) =>
          m.simulationName === simulationName &&
          m.simStepId === simStep.simStepId
      );

      if (!exists) {
        const updatedMappings = [...existingMappings, newMapping];

        // Update the repoData object
        useStore.getState().setRepoObjectEntry(repoPath, {
          simulationMappings: updatedMappings,
        });

        logger.debug.info(
          `Added reverse mapping for SimStep ${simStep.simStepId} to repoData object ${repoPath}`
        );
      }
    }
  });

  // Also update the global simulation mappings in the store
  updateSimulationMappings();
}

/**
 * Removes a reverse mapping when a simulation step is deleted
 * This removes the reference from the repoData object
 */
export function removeReverseMapping(
  simulationName: string,
  simStepId: string
): void {
  const { repoData } = useStore.getState();

  logger.debug.info(
    `Removing reverse mapping for SimStep ${simStepId} from simulation ${simulationName}`
  );

  // Iterate through all repoData objects to find and remove the mapping
  Object.entries(repoData).forEach(([repoPath, repoDataObject]) => {
    if (repoDataObject.simulationMappings) {
      const filteredMappings = repoDataObject.simulationMappings.filter(
        (mapping: SimulationMapping) =>
          !(
            mapping.simulationName === simulationName &&
            mapping.simStepId === simStepId
          )
      );

      // Update if there were changes
      if (
        filteredMappings.length !== repoDataObject.simulationMappings.length
      ) {
        useStore.getState().setRepoObjectEntry(repoPath, {
          simulationMappings: filteredMappings,
        });

        logger.debug.info(
          `Removed reverse mapping for SimStep ${simStepId} from repoData object ${repoPath}`
        );
      }
    }
  });

  // Also update the global simulation mappings in the store
  updateSimulationMappings();
}

/**
 * Updates reverse mappings for all simulation steps in a simulation
 * Useful when a simulation is renamed or modified
 */
export function updateReverseMappingsForSimulation(
  simulation: Simulation
): void {
  logger.debug.info(
    `Updating reverse mappings for simulation ${simulation.name} with ${simulation.simSteps.length} steps`
  );

  simulation.simSteps.forEach((simStep) => {
    addReverseMapping(simulation.name, simStep);
  });
}

/**
 * Finds repoData objects that match the given file path and line range
 */
function findMatchingRepoDataPaths(
  filePath: string,
  startLine: number,
  endLine: number,
  repoData: any
): string[] {
  const matchingPaths: string[] = [];

  Object.entries(repoData).forEach(
    ([repoPath, repoDataObject]: [string, any]) => {
      // Skip if no line information
      if (!repoDataObject.startLine || !repoDataObject.endLine) {
        return;
      }

      // Check if the file paths match
      const repoObjectFilePath = getFilePathFromRepoData(
        repoDataObject,
        repoData
      );
      if (repoObjectFilePath !== filePath) {
        return;
      }

      // Check if line ranges overlap
      const repoStartLine = parseInt(repoDataObject.startLine);
      const repoEndLine = parseInt(repoDataObject.endLine);

      if (lineRangesOverlap(startLine, endLine, repoStartLine, repoEndLine)) {
        matchingPaths.push(repoPath);
      }
    }
  );

  return matchingPaths;
}

/**
 * Gets the file path for a repoData object by looking at its parent
 */
function getFilePathFromRepoData(repoDataObject: any, repoData: any): string {
  // If object has fileName and parentPath, construct the full path
  if (repoDataObject.fileName && repoDataObject.parentPath) {
    return repoDataObject.parentPath;
  }

  // If object has parentPath, use it directly
  if (repoDataObject.parentPath) {
    return repoDataObject.parentPath;
  }

  // If object has a path that looks like a file path
  if (repoDataObject.path && repoDataObject.path.includes("/")) {
    return repoDataObject.path;
  }

  return "";
}

/**
 * Checks if two line ranges overlap
 */
function lineRangesOverlap(
  start1: number,
  end1: number,
  start2: number,
  end2: number
): boolean {
  return start1 <= end2 && start2 <= end1;
}

/**
 * Updates simulation mappings in the store
 * Should be called whenever simulations are loaded or modified
 */
export function updateSimulationMappings(): void {
  const mappings = buildSimulationMappings();
  useStore.getState().setSimulationMappings(mappings);
  logger.debug.info("Updated simulation mappings in store");
}

/**
 * Gets simulation mappings for a specific repoData path
 */
export function getSimulationMappingsForPath(
  path: string
): SimulationMapping[] {
  const { simulationMappings } = useStore.getState();
  return simulationMappings[path] || [];
}

/**
 * Checks if a repoData object has any simulation mappings
 */
export function hasSimulationMappings(path: string): boolean {
  const mappings = getSimulationMappingsForPath(path);
  return mappings.length > 0;
}
